import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Icon, Form, Container } from 'semantic-ui-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { get } from 'lodash';
import CryptoJS from 'crypto-js';

import { APP_NAME, LOGIN_ENCRYPTION_KEY } from '../../utils/constant';
import { loginUser } from '../../store/actions';
import { CustomInput, scrollToFirstErrorField } from '../../components/form';
import { userAuthMgr } from '../../utils/auth';
import { INSTANCE_CONTACT_EMAIL2 } from '../../utils/instanceConstant';

import {
  createErrorObject,
  formData,
  validationSchema,
} from '../../validations/signIn-validation';

import './auth.scss';

const SignIn = (props) => {
  const { t } = useTranslation();
  const authentication = useSelector((state) => state.authentication);
  // inject authentication data to props
  props = { ...props, ...authentication };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState({
    checkbox: false,
    email: null,
    password: null,
    redirectToReferrer: false,
    errorHandling: null,
    isShownPass: false,
    redirectUrl: '/dashboard',
  });

  useEffect(() => {
    document.title = `${t('auth.pages.sign_in')} | ${APP_NAME}`;

    const redirectUrl = get(
      props.location.state,
      'redirectRoute',
      '/dashboard'
    );
    const logoutReason = get(props.location.state, 'logoutReason', null);

    setState((prevState) => ({
      ...prevState,
      redirectUrl,
      errorHandling: logoutReason,
    }));
  }, [props.location]);

  const handleValidSubmit = async (values) => {
    try {
      const { redirectUrl } = state;
      await dispatch(
        loginUser({
          username: CryptoJS.AES.encrypt(
            values.email,
            LOGIN_ENCRYPTION_KEY
          ).toString(),
          password: CryptoJS.AES.encrypt(
            values.password,
            LOGIN_ENCRYPTION_KEY
          ).toString(),
        })
      );

      userAuthMgr.authenticate(() => {
        setState((prevState) => ({ ...prevState, redirectToReferrer: true }));
      });
      navigate(redirectUrl);
    } catch (error) {
      console.error('Login Error', error);
      setState((prevState) => ({ ...prevState, errorHandling: error }));
    }
  };

  const onSubmit = async (values) => {
    await handleValidSubmit(values);
  };

  const { userLoginLoading } = authentication;
  let { from } = props.location?.state || { from: { pathname: '/' } };
  let { redirectToReferrer, errorHandling, isShownPass } = state;

  if (redirectToReferrer) return <Navigate to={from} />;

  return (
    <Grid.Row className={'signin-page-wrapper'}>
      <Container className="right-heading-container">
        <label className="headerText">{t('auth.signin.title')}</label>
        <label className="headerDescription">{t('auth.signin.subtitle')}</label>
        <br></br>

        {errorHandling ? (
          <label className="errorBox">
            <label className="errorBoxContent">{errorHandling}</label>
          </label>
        ) : null}

        <Formik
          onSubmit={(values) => {
            onSubmit(values);
          }}
          initialValues={formData}
          validationSchema={validationSchema(t)}
        >
          {({ handleSubmit, errors, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              {scrollToFirstErrorField(createErrorObject(errors), isSubmitting)}
              <CustomInput
                id="email"
                name="email"
                autoComplete="username"
                placeholder={t('auth.signin.email')}
                labelText={t('auth.signin.email')}
                type="text"
                disabled={userLoginLoading}
              />
              <CustomInput
                id="password"
                name="password"
                autoComplete="current-password"
                placeholder={t('formulary.password')}
                labelText={t('formulary.password')}
                disabled={userLoginLoading}
                type={isShownPass ? 'text' : 'password'}
                icon={
                  <Icon
                    name={isShownPass ? 'eye' : 'eye slash'}
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        isShownPass: !isShownPass,
                      }));
                    }}
                    link
                  />
                }
              />
              <Link className="forgot" to="/forgot-password">
                {t('auth.signin.password_forgot')}
              </Link>{' '}
              <Button
                loading={userLoginLoading}
                disabled={userLoginLoading}
                content={t('actions.sign_in')}
                type="submit"
                className="btn btn-primary signin-button"
              />
              <label className={'alreadyContent'}>
                {t('auth.signin.new_user')}
                <a
                  href={`mailto:${INSTANCE_CONTACT_EMAIL2}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <label className={'signin-label'}>
                    {INSTANCE_CONTACT_EMAIL2}
                  </label>
                </a>
              </label>
            </Form>
          )}
        </Formik>
      </Container>
    </Grid.Row>
  );
};

export default SignIn;
